import type {
  ErrorCode,
  FoodBlob,
  GameEvent,
  RemoteSnake,
  RoomListing,
  ScoreRow,
  SelfState,
  ServerMessage,
} from '../shared/protocol.js';
import { startLobbyField, stopLobbyField } from './lobbyField.js';
import { makeView, render, resetTrail } from './render.js';
import { makeView3d, render3d } from './render3d.js';
import { loadLook, saveLook, selectedIds, WORM_SKINS, WORM_TEMPLATES, GLOW_COLORS, EYE_STYLES } from './wormLook.js';
import { unlockAudio, eatSound, boostSound } from './audio.js';

type HandlerBalances = {
  wallet: { btc: number; xmr: number };
  carried: { btc: number; xmr: number };
  text: { btc: string; xmr: string; carriedBtc: string; carriedXmr: string };
};

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const canvas = $<HTMLCanvasElement>('arena');
const view = makeView(canvas);
const canvas3d = document.getElementById('arena3d') as HTMLCanvasElement | null;
const view3d = canvas3d ? makeView3d(canvas3d) : null;

const el = {
  overlay: $('overlay'),
  cardName: $('card-name'),
  cardLobby: $('card-lobby'),
  cardDead: $('card-dead'),
  cardBoard: $('card-board'),
  inputBtc: $<HTMLInputElement>('input-btc'),
  inputXmr: $<HTMLInputElement>('input-xmr'),
  nameError: $('name-error'),
  lobbyError: $('lobby-error'),
  lobbyHello: $('lobby-hello'),
  lobbyBtc: $('lobby-btc'),
  lobbyXmr: $('lobby-xmr'),
  lobbySlither: $('lobby-slither'),
  lobbyKingdom: $('lobby-kingdom'),
  testNote: $('test-note'),
  testSign: $('test-sign'),
  lobbyField: $<HTMLCanvasElement>('lobby-field'),
  skinSheet: $('skin-sheet'),
  fundNote: $('fund-note'),
  fundAccount: $('fund-account'),
  lookBlurb: $('look-blurb'),
  rooms: $('rooms'),
  btnJoin: $<HTMLButtonElement>('btn-join'),
  btnRejoin: $<HTMLButtonElement>('btn-rejoin'),
  btnLobby: $<HTMLButtonElement>('btn-lobby'),
  btnBank: $<HTMLButtonElement>('btn-bank'),
  btnLeave: $<HTMLButtonElement>('btn-leave'),
  btnLogout: $<HTMLButtonElement>('btn-logout'),
  btnBoard: $<HTMLButtonElement>('btn-board-ok'),
  deadTitle: $('dead-title'),
  deadDetail: $('dead-detail'),
  finalRows: $('final-rows'),
  stats: $('stats'),
  leaderboard: $('leaderboard'),
  boardRows: $('board-rows'),
  killfeed: $('killfeed'),
  wallet: $('wallet'),
  controls: $('controls'),
  emotes: $('emotes'),
  statTime: $('stat-time'),
  statLength: $('stat-length'),
  statScore: $('stat-score'),
  statKills: $('stat-kills'),
  statCarry: $('stat-carry'),
  wBtc: $('w-btc'),
  wXmr: $('w-xmr'),
  wCarry: $('w-carry'),
};

const state = {
  socket: null as WebSocket | null,
  self: null as SelfState | null,
  snakes: [] as RemoteSnake[],
  food: [] as FoodBlob[],
  worldRadius: 6000,
  inRound: false,
  alive: false,
  seq: 0,
  boostHeld: false,
  angle: 0,
  deadShown: false,
  authed: false,
  name: '',
  accountId: '',
  fundNote: 'This wallet starts at 0. A personal BTC or XMR address is not connected yet, so nothing here can take a payment, and play does not add coins.',
  walletBtc: 0,
  walletXmr: 0,
  size: 'normal' as 'small' | 'normal' | 'all',
  pendingAutoJoin: false,
  testOrbs: true,
};

const EMOTES: Array<[string, string]> = [
  ['mad', 'mad'],
  ['happy', 'happy'],
  ['sad', 'sad'],
  ['cry', 'cry'],
  ['gg', 'GG'],
];

for (const [id, label] of EMOTES) {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.addEventListener('click', () => send({ t: 'emote', id }));
  el.emotes.append(b);
}

function show(which: 'name' | 'lobby' | 'dead' | 'board' | 'play'): void {
  const playing = which === 'play';
  document.body.classList.toggle('playing', playing);
  el.overlay.hidden = playing;
  el.cardName.hidden = which !== 'name';
  el.cardLobby.hidden = which !== 'lobby';
  el.cardDead.hidden = which !== 'dead';
  el.cardBoard.hidden = which !== 'board';
  el.lobbyField.hidden = playing;
  if (playing) stopLobbyField();
  else startLobbyField(el.lobbyField);
  el.testSign.hidden = !(playing && state.testOrbs);
  if (which === 'lobby') el.btnJoin.disabled = false;
  el.stats.hidden = !playing;
  el.leaderboard.hidden = !playing;
  el.killfeed.hidden = !playing;
  el.wallet.hidden = true;
  el.controls.hidden = !playing;
}

function paintTest(on: boolean): void {
  state.testOrbs = on;
  el.testNote.hidden = !on;
  el.testSign.hidden = !on || !el.overlay.hidden;
  const stakes = document.getElementById('stake-sizes');
  if (stakes) stakes.hidden = on;
  el.btnBank.hidden = on;
  if (on) el.statCarry.textContent = 'No XMR or BTC';
}

type Me = {
  name: string;
  accountId?: string;
  btc: number;
  xmr: number;
  btcText: string;
  xmrText: string;
  funding?: { live?: boolean; note?: string };
};

const LOOKS: Record<string, string> = {
  warden: 'Warden keeps the neon gate. Armor, a short blade, and the city behind them.',
  glitch: 'Glitch is a body made of broken light. Fast, strange, hard to look at.',
  mason: 'Mason raises the buildings. Blocks, bridges, and rooms that remember you.',
  hex: 'Hex calls the monsters. Some of them stay. Some of them don\'t.',
};

function authMessage(code: string): string {
  if (code === 'expired') return 'That login expired. Try again from here.';
  if (code === 'state') return 'That login did not match this browser. Try again.';
  return 'ZETOP could not open the lobby. Try again.';
}

async function boot(): Promise<void> {
  const hash = new URLSearchParams(location.hash.startsWith('#') ? location.hash.slice(1) : '');
  const ticket = hash.get('ticket');
  if (location.hash || location.search) history.replaceState({}, '', '/play');
  if (ticket) {
    try {
      const res = await fetch('/api/session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ticket }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        el.nameError.textContent = authMessage(body.error ?? 'bad');
      }
    } catch {
      el.nameError.textContent = 'Could not reach the game server.';
    }
  }
  const res = await fetch('/api/me');
  if (res.ok) {
    const me = await res.json() as Me;
    takeMe(me);
    show('lobby');
    connect();
    return;
  }
  show('name');
}

function takeMe(me: Me): void {
  state.authed = true;
  state.name = me.name;
  state.accountId = me.accountId ?? state.accountId;
  if (me.funding?.note) state.fundNote = me.funding.note;
  remember(me.btc, me.xmr, me.btcText, me.xmrText);
}

function paintWallet(btc: string, xmr: string): void {
  el.lobbyHello.textContent = state.name;
  el.lobbyBtc.textContent = btc.replace(/ BTC$/, '');
  el.lobbyXmr.textContent = xmr.replace(/ XMR$/, '');
  el.wBtc.textContent = btc;
  el.wXmr.textContent = xmr;
  el.fundAccount.textContent = state.accountId || '—';
  el.fundNote.textContent = state.fundNote;
}

function remember(btc: number, xmr: number, btcText: string, xmrText: string): void {
  state.walletBtc = btc;
  state.walletXmr = xmr;
  paintWallet(btcText, xmrText);
  applySize(state.size);
}

function picoToInput(pico: number): string {
  if (pico <= 0) return '0';
  return (pico / 1e12).toFixed(12).replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
}

function applySize(size: 'small' | 'normal' | 'all'): void {
  state.size = size;
  const btc = state.walletBtc;
  const xmr = state.walletXmr;
  let nextBtc = 0;
  let nextXmr = 0;
  if (size === 'small') {
    nextBtc = Math.min(1, btc);
  } else if (size === 'all') {
    nextBtc = Math.min(50_000_000, btc);
    nextXmr = xmr;
  } else {
    nextBtc = Math.min(12, btc);
    nextXmr = Math.min(18_400_000, xmr);
  }
  el.inputBtc.value = String(nextBtc);
  el.inputXmr.value = picoToInput(nextXmr);
  for (const btn of document.querySelectorAll<HTMLButtonElement>('.size')) {
    btn.classList.toggle('on', btn.dataset.size === size);
  }
}

for (const btn of document.querySelectorAll<HTMLButtonElement>('.size')) {
  btn.addEventListener('click', () => {
    const size = btn.dataset.size;
    if (size === 'small' || size === 'normal' || size === 'all') applySize(size);
  });
}

function connect(): void {
  if (state.socket && state.socket.readyState <= WebSocket.OPEN) return;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const socket = new WebSocket(`${proto}://${location.host}/ws`);
  state.socket = socket;
  socket.addEventListener('message', (ev) => {
    try { handle(JSON.parse(String(ev.data)) as ServerMessage); } catch { /* ignore */ }
  });
  socket.addEventListener('close', () => {
    state.socket = null;
    state.inRound = false;
    if (state.authed) setTimeout(connect, 800);
  });
  socket.addEventListener('error', () => socket.close());
}

function send(msg: unknown): void {
  const socket = state.socket;
  if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
}

function handle(msg: ServerMessage): void {
  switch (msg.t) {
    case 'welcome':
      if (msg.balances && msg.text) remember(msg.balances.btc, msg.balances.xmr, msg.text.btc, msg.text.xmr);
      else if (msg.text) paintWallet(msg.text.btc, msg.text.xmr);
      if (msg.rooms) renderRooms(msg.rooms);
      if (typeof msg.testOrbs === 'boolean') paintTest(msg.testOrbs);
      if (state.pendingAutoJoin) {
        state.pendingAutoJoin = false;
        joinRoom();
        break;
      }
      if (!state.inRound) show('lobby');
      break;
    case 'lobby':
      remember(msg.balances.btc, msg.balances.xmr, msg.text.btc, msg.text.xmr);
      renderRooms(msg.rooms);
      if (typeof msg.testOrbs === 'boolean') paintTest(msg.testOrbs);
      state.inRound = false;
      state.self = null;
      state.snakes = [];
      state.food = [];
      if (deathTimer) { window.clearTimeout(deathTimer); deathTimer = 0; }
      show('lobby');
      break;
    case 'balances':
      applyBalances(msg);
      break;
    case 'snapshot':
      onSnapshot(msg);
      break;
    case 'died':
      showDeath(msg.reason, msg.canRejoin);
      break;
    case 'scoreboard':
      showBoard(msg.rows);
      break;
    case 'error':
      onError(msg.code, msg.message);
      break;
    default:
      break;
  }
}

function applyBalances(msg: HandlerBalances): void {
  remember(msg.wallet.btc, msg.wallet.xmr, msg.text.btc, msg.text.xmr);
  el.wCarry.textContent = `${msg.text.carriedBtc} · ${msg.text.carriedXmr}`;
  el.statCarry.textContent = state.testOrbs
    ? 'No XMR or BTC'
    : (msg.carried.btc > 0 || msg.carried.xmr > 0 ? `${msg.text.carriedBtc} · ${msg.text.carriedXmr}` : '0');
}

function onSnapshot(msg: Extract<ServerMessage, { t: 'snapshot' }>): void {
  state.self = msg.self;
  const prevSeg = (state as any)._lastSegs ?? 0;
  const wasBoosting = (state as any)._lastBoosting ?? false;
  state.snakes = msg.snakes;
  state.food = msg.food;
  state.alive = msg.self?.alive ?? false;
  if (msg.round) el.statTime.textContent = clock(msg.round.secondsLeft);
  if (typeof msg.testOrbs === 'boolean') paintTest(msg.testOrbs);
  if (msg.self && msg.self.alive && msg.self.segments > prevSeg) eatSound();
  if (msg.self && msg.self.boosting && !wasBoosting) boostSound();
  (state as any)._lastSegs = msg.self?.segments ?? 0;
  (state as any)._lastBoosting = msg.self?.boosting ?? false;

  const me = msg.self;
  if (me && state.inRound) {
    el.statLength.textContent = String(me.segments);
    el.statScore.textContent = String(me.segments * 4);
    el.statKills.textContent = `${me.kills} kills`;
    renderBoardFromSnakes(me, msg.snakes);
  }
  if (msg.events.length > 0) pushFeed(msg.events);
  if (state.inRound && state.alive) show('play');
}

function renderRooms(rooms: RoomListing[]): void {
  if (rooms.length === 0) {
    el.rooms.textContent = 'Arena is open';
    return;
  }
  const players = rooms.reduce((sum, room) => sum + room.players, 0);
  const live = rooms.find((room) => room.secondsLeft !== null);
  const clockBit = live ? `${clock(live.secondsLeft ?? 0)} left` : 'waiting for the first worm';
  el.rooms.textContent = rooms.length === 1
    ? `${players}/${rooms[0]!.cap} in · ${clockBit}`
    : `${rooms.length} arenas · ${players} worms`;
}

function renderBoardFromSnakes(self: SelfState, snakes: RemoteSnake[]): void {
  const rows = [
    { name: self.name, score: self.segments, self: true },
    ...snakes.map((s) => ({ name: s.name, score: Math.floor(s.body.length / 3), self: false })),
  ];
  rows.sort((a, b) => b.score - a.score);
  el.boardRows.replaceChildren(...rows.slice(0, 8).map((row, i) => {
    const li = document.createElement('li');
    if (row.self) li.className = 'self';
    const rank = document.createElement('span');
    rank.className = 'rank';
    rank.textContent = `${i + 1}.`;
    const name = document.createElement('span');
    name.className = 'nm';
    name.textContent = row.name;
    const score = document.createElement('span');
    score.className = 'sc';
    score.textContent = String(row.score);
    li.append(rank, name, score);
    return li;
  }));
}

const feedLines: string[] = [];
let deathTimer = 0;

function pushFeed(events: GameEvent[]): void {
  for (const ev of events) {
    if (ev.type === 'kill') {
      feedLines.unshift(`${ev.killer} ate ${ev.victim}`);
    } else if (ev.type === 'death' && ev.reason !== 'rival') {
      feedLines.unshift(`you died (${ev.reason})`);
    }
  }
  feedLines.length = Math.min(feedLines.length, 5);
  el.killfeed.replaceChildren(...feedLines.map((line) => {
    const div = document.createElement('div');
    div.textContent = line;
    return div;
  }));
}

function showDeath(reason: string, canRejoin: boolean): void {
  if (state.deadShown) return;
  state.deadShown = true;
  state.alive = false;
  const length = state.self?.segments ?? 0;
  el.deadTitle.textContent = 'Popped';
  el.deadDetail.textContent = state.testOrbs
    ? `Final score ${length * 4} at length ${length}. That score is not XMR or BTC.`
    : (canRejoin ? 'Your coins are on the map. Grab them.' : `Caught (${reason}).`);
  el.btnRejoin.hidden = !canRejoin;
  if (deathTimer) window.clearTimeout(deathTimer);
  deathTimer = window.setTimeout(() => {
    deathTimer = 0;
    show('dead');
  }, 2000);
}

function showBoard(rows: ScoreRow[]): void {
  state.inRound = false;
  state.deadShown = false;
  el.finalRows.replaceChildren(...rows.map((row) => {
    const block = document.createElement('div');
    block.className = row.isSelf ? 'final self' : 'final';
    const title = document.createElement('strong');
    title.textContent = `${row.name} · ${row.kills} kills`;
    const detail = document.createElement('p');
    detail.textContent = state.testOrbs
      ? 'Test round. No XMR or BTC was added or taken.'
      : `Left with ${row.end}`;
    block.append(title, detail);
    return block;
  }));
  show('board');
}

function onError(code: ErrorCode, message: string): void {
  el.btnJoin.disabled = false;
  if (code === 'UNAUTHORIZED') {
    state.authed = false;
    state.inRound = false;
    show('name');
    return;
  }
  el.lobbyError.textContent = message;
  if (!state.alive) {
    state.inRound = false;
    show('lobby');
  }
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function joinRoom(): void {
  if (!state.socket || state.socket.readyState !== WebSocket.OPEN) {
    el.lobbyError.textContent = 'Still connecting. Try again in a second.';
    return;
  }
  const btc = state.testOrbs ? 0 : Math.floor(Number(el.inputBtc.value));
  if (!Number.isSafeInteger(btc) || btc < 0) {
    el.lobbyError.textContent = 'Stake sats as a whole number.';
    return;
  }
  el.lobbyError.textContent = '';
  el.btnJoin.disabled = true;
  resetTrail();
  feedLines.length = 0;
  state.inRound = true;
  state.deadShown = false;
  state.self = null;
  send({
    t: 'join',
    btc,
    xmr: state.testOrbs ? '0' : (el.inputXmr.value.trim() || '0'),
    nonce: crypto.randomUUID(),
  });
  show('play');
}

function showGame(which: 'slither' | 'kingdom'): void {
  el.lobbySlither.hidden = which !== 'slither';
  el.lobbyKingdom.hidden = which !== 'kingdom';
  for (const tab of document.querySelectorAll<HTMLButtonElement>('.game')) {
    const on = tab.dataset.game === which;
    tab.classList.toggle('on', on);
    tab.setAttribute('aria-selected', on ? 'true' : 'false');
  }
}

for (const tab of document.querySelectorAll<HTMLButtonElement>('.game')) {
  tab.addEventListener('click', () => {
    const which = tab.dataset.game;
    if (which === 'slither' || which === 'kingdom') showGame(which);
  });
}

function showLook(id: string): void {
  const blurb = LOOKS[id] ?? LOOKS.warden;
  el.lookBlurb.textContent = blurb ?? '';
  for (const btn of document.querySelectorAll<HTMLButtonElement>('.look')) {
    btn.classList.toggle('on', btn.dataset.look === id);
  }
  try { sessionStorage.setItem('ztg-kingdom-look', id); } catch { /* private mode */ }
}

for (const btn of document.querySelectorAll<HTMLButtonElement>('.look')) {
  btn.addEventListener('click', () => {
    const id = btn.dataset.look;
    if (id && LOOKS[id]) showLook(id);
  });
}
try {
  const saved = sessionStorage.getItem('ztg-kingdom-look');
  if (saved && LOOKS[saved]) showLook(saved);
} catch { /* private mode */ }

el.btnJoin.addEventListener('click', joinRoom);
function paintSkins(): void {
  const picked = selectedIds();
  const colors = document.getElementById('skin-colors');
  const templates = document.getElementById('skin-templates');
  const glows = document.getElementById('skin-glow');
  const eyes = document.getElementById('skin-eyes');
  if (!colors || !templates || !glows || !eyes) return;
  colors.replaceChildren();
  templates.replaceChildren();
  glows.replaceChildren();
  eyes.replaceChildren();
  for (const skin of WORM_SKINS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sk';
    b.style.setProperty('--c', skin.a);
    b.style.background = `linear-gradient(135deg, ${skin.a}, ${skin.b})`;
    b.setAttribute('aria-label', skin.name);
    b.setAttribute('aria-pressed', skin.id === picked.skin ? 'true' : 'false');
    b.addEventListener('click', () => {
      saveLook(skin.id, picked.template, picked.glow, picked.eyes);
      paintSkins();
    });
    colors.append(b);
  }
  for (const template of WORM_TEMPLATES) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tm';
    b.textContent = template.name;
    b.setAttribute('aria-pressed', template.id === picked.template ? 'true' : 'false');
    b.addEventListener('click', () => {
      saveLook(picked.skin, template.id, picked.glow, picked.eyes);
      paintSkins();
    });
    templates.append(b);
  }
  for (const glow of GLOW_COLORS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sk';
    b.style.setProperty('--c', glow.c);
    b.style.background = glow.c;
    b.setAttribute('aria-label', glow.name);
    b.setAttribute('aria-pressed', glow.c === picked.glow ? 'true' : 'false');
    b.addEventListener('click', () => {
      saveLook(picked.skin, picked.template, glow.c, picked.eyes);
      paintSkins();
    });
    glows.append(b);
  }
  for (const eye of EYE_STYLES) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tm';
    b.textContent = eye.name;
    b.setAttribute('aria-pressed', eye.id === picked.eyes ? 'true' : 'false');
    b.addEventListener('click', () => {
      saveLook(picked.skin, picked.template, picked.glow, eye.id);
      paintSkins();
    });
    eyes.append(b);
  }
}
paintSkins();
document.getElementById('btn-skins')?.addEventListener('click', () => { el.skinSheet.hidden = false; });
document.getElementById('btn-skins-close')?.addEventListener('click', () => { el.skinSheet.hidden = true; });
el.skinSheet.addEventListener('click', (ev) => {
  if (ev.target === el.skinSheet) el.skinSheet.hidden = true;
});
window.addEventListener('keydown', (ev) => {
  if (ev.key !== 'Enter' || el.cardLobby.hidden || el.lobbySlither.hidden) return;
  if (ev.target instanceof HTMLButtonElement) return;
  joinRoom();
});
el.btnBank.addEventListener('click', () => send({ t: 'bank' }));
el.btnLeave.addEventListener('click', () => send({ t: 'bank' }));
el.btnLogout.addEventListener('click', () => {
  void logOut();
});

async function logOut(): Promise<void> {
  if (deathTimer) { window.clearTimeout(deathTimer); deathTimer = 0; }
  state.authed = false;
  state.pendingAutoJoin = false;
  state.inRound = false;
  state.name = '';
  state.self = null;
  state.snakes = [];
  state.food = [];
  try {
    await fetch('/api/logout', { method: 'POST' });
  } catch { /* the cookie clear is best-effort */ }
  state.socket?.close();
  state.socket = null;
  state.accountId = '';
  show('name');
}
el.btnRejoin.addEventListener('click', () => {
  if (deathTimer) { window.clearTimeout(deathTimer); deathTimer = 0; }
  state.deadShown = false;
  resetTrail();
  send({ t: 'rejoin', nonce: crypto.randomUUID() });
  show('play');
});
el.btnLobby.addEventListener('click', () => {
  if (deathTimer) { window.clearTimeout(deathTimer); deathTimer = 0; }
  state.deadShown = false;
  state.inRound = false;
  send({ t: 'lobby' });
});
el.btnBoard.addEventListener('click', () => send({ t: 'lobby' }));

window.addEventListener('mousemove', (ev) => {
  if (!state.self) return;
  state.angle = Math.atan2(ev.clientY - window.innerHeight / 2, ev.clientX - window.innerWidth / 2);
});
const boost = document.getElementById('boost');
boost?.addEventListener('pointerdown', (ev) => {
  ev.preventDefault();
  ev.stopPropagation();
  state.boostHeld = true;
});
boost?.addEventListener('pointerup', () => { state.boostHeld = false; });
boost?.addEventListener('pointercancel', () => { state.boostHeld = false; });
window.addEventListener('mousedown', (ev) => { if (ev.button === 0) state.boostHeld = true; });
window.addEventListener('mouseup', () => { state.boostHeld = false; });
window.addEventListener('keydown', (ev) => {
  if (ev.code === 'Space') { state.boostHeld = true; ev.preventDefault(); }
});
window.addEventListener('keydown', unlockAudio);
window.addEventListener('pointerdown', unlockAudio);
window.addEventListener('keyup', (ev) => { if (ev.code === 'Space') state.boostHeld = false; });
window.addEventListener('blur', () => { state.boostHeld = false; });

let touchId: number | null = null;
let lastTap = 0;
canvas.addEventListener('touchstart', (ev) => {
  const t = ev.changedTouches[0];
  if (!t) return;
  touchId = t.identifier;
  state.angle = Math.atan2(t.clientY - window.innerHeight / 2, t.clientX - window.innerWidth / 2);
  const now = performance.now();
  if (now - lastTap < 320) {
    state.boostHeld = true;
    lastTap = 0;
  } else {
    state.boostHeld = false;
    lastTap = now;
  }
  ev.preventDefault();
}, { passive: false });
canvas.addEventListener('touchmove', (ev) => {
  for (const t of Array.from(ev.changedTouches)) {
    if (t.identifier !== touchId) continue;
    state.angle = Math.atan2(t.clientY - window.innerHeight / 2, t.clientX - window.innerWidth / 2);
  }
  ev.preventDefault();
}, { passive: false });
const endTouch = (ev: TouchEvent): void => {
  for (const t of Array.from(ev.changedTouches)) {
    if (t.identifier === touchId) { touchId = null; state.boostHeld = false; }
  }
};
canvas.addEventListener('touchend', endTouch);
canvas.addEventListener('touchcancel', endTouch);

setInterval(() => {
  if (!state.inRound || !state.alive) return;
  send({ t: 'input', angle: state.angle, boost: state.boostHeld, seq: ++state.seq });
}, 33);

function frame(): void {
  const now = performance.now();
  const look = loadLook();
  if (view3d?.ok && location.search.indexOf('view3d=1') !== -1) {
    render3d(view3d, state.self, state.snakes, state.food, state.worldRadius, now, {
      testOrbs: state.testOrbs,
      look,
    });
    render(view, state.self, state.snakes, state.food, state.worldRadius, now, { hudOnly: true, look });
  } else {
    render(view, state.self, state.snakes, state.food, state.worldRadius, now, {
      testOrbs: state.testOrbs,
      look,
    });
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

void boot();
